import React from 'react';
import {
	Box,
	Flex,
	Text,
	Icon,
	Badge,
	useColorModeValue,
} from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FiHome, FiUsers, FiAlertTriangle, FiMenu } from 'react-icons/fi';
import { FaSitemap } from 'react-icons/fa';
import { useUnreadEvents } from '../../context/UnreadEventsContext';

/**
 * Componente: BottomNavigation
 * Barra de navegación inferior fija para teléfonos móviles (Mobile-First Thumb-Zone).
 * Cumple con directrices Apple HIG y WCAG 2.5.5 (mínimo 44x44px táctiles).
 */
export const BottomNavigation = ({ onOpenDrawer }: { onOpenDrawer?: () => void }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { user } = useSelector((state: any) => state.auth || {});
	const { allEvents } = useUnreadEvents();
	const unreadCount = allEvents?.length || 0;

	const isSuperAdmin = Boolean(user?.isAdmin === true);
	const isCorporation = user?.role === 'corporacion';
	const pathname = location.pathname;

	const isHomeActive = pathname === '/home' || pathname === '/';
	const isCorporationsActive = pathname.startsWith('/corporations');
	const isPatientsActive = pathname.startsWith('/patients');
	const isEventsActive = pathname.startsWith('/events');

	const bg = useColorModeValue('white', 'gray.800');
	const borderColor = useColorModeValue('gray.200', 'gray.700');
	const activeColor = 'teal.600';
	const inactiveColor = 'gray.500';

	const navItems = [
		{
			id: 'home',
			label: t('Home') || 'Inicio',
			icon: FiHome,
			isActive: isHomeActive,
			onClick: () => navigate('/home'),
			badge: null,
		},
		...(isSuperAdmin
			? [
					{
						id: 'corporations',
						label: t('Corporations') || 'Corporaciones',
						icon: FaSitemap,
						isActive: isCorporationsActive,
						onClick: () => navigate('/corporations'),
						badge: null,
					},
			  ]
			: [
					{
						id: 'patients',
						label: t('Patients') || 'Pacientes',
						icon: FiUsers,
						isActive: isPatientsActive,
						onClick: () => navigate('/patients'),
						badge: null,
					},
			  ]),
		...(!isSuperAdmin && !isCorporation
			? [
					{
						id: 'events',
						label: t('Events') || 'Alertas',
						icon: FiAlertTriangle,
						isActive: isEventsActive,
						onClick: () => navigate('/events'),
						badge: unreadCount > 0 ? (unreadCount > 99 ? '99+' : unreadCount) : null,
					},
			  ]
			: []),
		{
			id: 'menu',
			label: t('More') || 'Menú',
			icon: FiMenu,
			isActive: false,
			onClick: () => onOpenDrawer?.(),
			badge: null,
		},
	];

	return (
		<Box
			as='nav'
			aria-label={t('Mobile primary navigation') || 'Navegación principal móvil'}
			position='fixed'
			bottom='0'
			left='0'
			right='0'
			zIndex='docked'
			bg={bg}
			borderTop='1px'
			borderColor={borderColor}
			boxShadow='0 -2px 10px rgba(0, 0, 0, 0.05)'
			display={{ base: 'block', xl: 'none' }}
			style={{
				paddingBottom: 'env(safe-area-inset-bottom, 0px)',
			}}
		>
			<Flex
				h='56px'
				align='center'
				justify='space-around'
				maxW='500px'
				mx='auto'
				px={2}
			>
				{navItems.map(item => {
					const IconComponent = item.icon;
					const color = item.isActive ? activeColor : inactiveColor;
					const fontWeight = item.isActive ? 'bold' : 'medium';

					return (
						<Flex
							key={item.id}
							as='button'
							type='button'
							direction='column'
							align='center'
							justify='center'
							flex='1'
							h='100%'
							minW='48px'
							minH='48px'
							py={1}
							px={1}
							cursor='pointer'
							bg='transparent'
							border='none'
							outline='none'
							transition='all 0.15s ease'
							_active={{ transform: 'scale(0.92)' }}
							onClick={item.onClick}
							aria-label={item.label}
							aria-current={item.isActive ? 'page' : undefined}
						>
							<Box position='relative' display='inline-block'>
								<Icon
									as={IconComponent}
									boxSize='20px'
									color={color}
									strokeWidth={item.isActive ? 2.5 : 2}
								/>
								{item.badge && (
									<Badge
										position='absolute'
										top='-6px'
										right='-10px'
										colorScheme='red'
										variant='solid'
										borderRadius='full'
										fontSize='9px'
										px='5px'
										py='1px'
										lineHeight='1'
										boxShadow='0 0 0 1.5px white'
									>
										{item.badge}
									</Badge>
								)}
							</Box>
							<Text
								fontSize='10px'
								color={color}
								fontWeight={fontWeight}
								mt='2px'
								lineHeight='1.1'
								isTruncated
								maxW='64px'
							>
								{item.label}
							</Text>
						</Flex>
					);
				})}
			</Flex>
		</Box>
	);
};

export default BottomNavigation;
